import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as fromActions from '../actions/surveys.actions';
import { USER_SURVEY_STATUS } from '../enums/user-survey-status.enum';
import { ISurveysFeatureState } from '../interfaces/states/surveys-feature-state.interface';
import { Survey } from '../interfaces/survey.interface';
import { SurveysService } from '../services/surveys.service';

@Injectable()
export class SurveysEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly service: SurveysService,
        private readonly store: Store<ISurveysFeatureState>,
    ) {}

    createSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createSurvey.createSurvey.type),
            mergeMap(() => {
                return this.service.createSurvey$().pipe(
                    map((response: Survey) => {
                        return fromActions.createSurvey.createSurveySuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.createSurvey.createSurveyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    stopSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.stopSurvey.stopSurvey.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.stopSurvey$(action.payload).pipe(
                    map((response: Survey) => {
                        return fromActions.stopSurvey.stopSurveySuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.stopSurvey.stopSurveyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    downloadSurveyCsv$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.downloadSurveyCsv.downloadSurveyCsv.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.downloadSurveyCsv$(action.payload).pipe(
                    map((blob: Blob) => {
                        // Trigger file download in the browser
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'survey.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);

                        return fromActions.downloadSurveyCsv.downloadSurveyCsvSuccess(); // no payload needed
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.downloadSurveyCsv.downloadSurveyCsvFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getActiveSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getActiveSurvey.getActiveSurvey.type),
            mergeMap(() =>
                this.service.getActiveSurvey$().pipe(
                    map((response: Survey) => {
                        return fromActions.getActiveSurvey.getActiveSurveySuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getActiveSurvey.getActiveSurveyFailed({ payload: error }));
                    }),
                ),
            ),
        ),
    );

    getSurveyById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getSurveyById.getSurveyById.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getSurveyById$(action.payload).pipe(
                    map((response) => {
                        return fromActions.getSurveyById.getSurveyByIdSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getSurveyById.getSurveyByIdFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getEligibleUsersSurveyStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatus.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getEligibleUsersSurveyStatus$(action.payload).pipe(
                    map((response) => {
                        return fromActions.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatusSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatusFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getSurveyResponses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getSurveyResponses.getSurveyResponses.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getSurveyResponses$(action.payload).pipe(
                    map((response) => {
                        return fromActions.getSurveyResponses.getSurveyResponsesSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getSurveyResponses.getSurveyResponsesFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAllSurveys$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAllSurveys.getAllSurveys.type),
            mergeMap(() => {
                return this.service.getAllSurveys$().pipe(
                    map((response) => {
                        return fromActions.getAllSurveys.getAllSurveysSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getAllSurveys.getAllSurveysFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    checkUserEligibility$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.checkUserEligibility.checkUserEligibility.type),
            mergeMap((surveyId) =>
                this.service.checkUserEligibility$(surveyId).pipe(
                    map((response: boolean) => {
                        return fromActions.checkUserEligibility.checkUserEligibilitySuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.checkUserEligibility.checkUserEligibilityFailed({ payload: error }));
                    }),
                ),
            ),
        ),
    );

    saveResponse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.saveResponse.saveResponse.type),
            mergeMap(({ surveyId, questionNumber, response }) =>
                this.service.saveResponse$(surveyId, questionNumber, response).pipe(
                    map(() => {
                        return fromActions.saveResponse.saveResponseSuccess({ questionNumber, response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.saveResponse.saveResponseFailed({ payload: error }));
                    }),
                ),
            ),
        ),
    );

    dismissSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.dismissSurvey.dismissSurvey.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.updateStatus$(action.payload, USER_SURVEY_STATUS.DISMISSED).pipe(
                    map((response) => {
                        return fromActions.dismissSurvey.dismissSurveySuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.dismissSurvey.dismissSurveyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    startSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.startSurvey.startSurvey.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.updateStatus$(action.payload, USER_SURVEY_STATUS.STARTED).pipe(
                    map((response) => {
                        return fromActions.startSurvey.startSurveySuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.startSurvey.startSurveyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getUserSurveyStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getUserSurveyStatus.getUserSurveyStatus.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getUserSurveyStatus$(action.payload).pipe(
                    map((response) => {
                        return fromActions.getUserSurveyStatus.getUserSurveyStatusSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getUserSurveyStatus.getUserSurveyStatusFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getUsersToNotify$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getUsersToNotify.getUsersToNotify.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getUsersToNotify$(action.payload).pipe(
                    map((response) => {
                        return fromActions.getUsersToNotify.getUsersToNotifySuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getUsersToNotify.getUsersToNotifyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    markUserAsNotified$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.markUserAsNotified.markUserAsNotified.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.markUserAsNotified$(action.payload).pipe(
                    map((response) => {
                        return fromActions.markUserAsNotified.markUserAsNotifiedSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.markUserAsNotified.markUserAsNotifiedFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    completeSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.completeSurvey.completeSurvey.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.updateStatus$(action.payload, USER_SURVEY_STATUS.COMPLETED).pipe(
                    map((response) => {
                        return fromActions.completeSurvey.completeSurveySuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.completeSurvey.completeSurveyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );
}
